import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldText,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { Checkbox, Input, Select, TextArea } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ServicesProvided from "../common/ServicesProvided";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getRecordedServices } from "../services/soar";
import { getSchema } from "./joi/validation";

function COTFCDischargeSummaryv2(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [recordedServices, setRecordedServices] = useState([]);

  //Get Client Info
  useEffect(() => {
    if (!clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await getCareTeams(clientid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setCareTeams(response);
    };

    loadClientInfo();
  }, [clientid]);

  useEffect(() => {
    if (!submissionid) {
      return;
    }

    const loadSubmission = async () => {
      const response = await getSubmission(submissionid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setData(response.Data);
      setFormsyDetails(response.FormsyDetails);

      if (response.Comments) {
        setComments(response.Comments);
      }

      if (response.Signatures) {
        setSignatures(response.Signatures);
      }
    };

    loadSubmission();
  }, [submissionid]);

  const dischargeReasonTFCOptions = [
    { _id: "186460002", name: "Adoption" },
    {
      _id: "186460013",
      name: "Client transferred with Foster Home to another division",
    },
    { _id: "186460007", name: `Death of child` },
    { _id: "186460003", name: "Emancipation" },
    { _id: "186460010", name: "Entry Error" },
    { _id: "186460004", name: "Guardianship" },
    { _id: "186460008", name: "Higher level of care*" },
    { _id: "186460001", name: "Living with other relative(s)" },
    { _id: "186460011", name: "Placement for Respite only" },
    { _id: "186460005", name: "Return to County/Transfer to another agency*" },
    {
      _id: "186460000",
      name: "Reunification with Parent(s) or Primary Caretaker(s)",
    },
    { _id: "186460006", name: "Runaway*" },
    { _id: "186460009", name: "Stepdown" },
    { _id: "186460012", name: "Transfer to another SAFY Division" },
  ];

  const dischargeReasonFPOptions = [
    { _id: "186460001", name: "Adoption" },
    { _id: "186460002", name: "Child aged out of services" },
    {
      _id: "186460015",
      name: "Child(ren) removed from home",
    },
    { _id: "186460008", name: "Family relocation" },
    { _id: "186460006", name: "Family/youth discontinued services" },
    { _id: "186460007", name: "Funding lost" },
    { _id: "186460005", name: "Higher level of care" },
    { _id: "186460012", name: "Inappropriate Referral" },
    { _id: "186460009", name: "Non-compliance to treatment recommendations" },
    { _id: "186460014", name: "Remained in Home" },
    {
      _id: "186460000",
      name: "Reunification with Caregiver",
    },
    { _id: "186460010", name: "Reunification/Discharge to Relative" },
    { _id: "186460004", name: "Runaway" },
    { _id: "186460013", name: "Transfer to another SAFY Division" },
    { _id: "186460003", name: "Treatment goals met" },
    { _id: "186460011", name: "Entry Error" },
  ];

  const localCrisisCenterOptions = [
    {
      _id: "1",
      name: "Larimer County",
      centerName: "SummitStone Health Partners",
      phone: "(844) 493-TALK(8255) or call (970) 494-4200 locally",
      address: "1217 Riverside Ave. Fort Collins, CO",
    },
    {
      _id: "2",
      name: "Boulder County",
      centerName: "Mental Health Partners",
      phone: "(303) 447-1665",
      address: "3180 Airport Rd. Boulder, CO",
    },
    {
      _id: "3",
      name: "Weld County",
      centerName: "North Range Behavioral Health",
      phone: "(970) 347-2120",
      address: "928 12th St. Greeley, CO",
    },
    {
      _id: "4",
      name: "Arapahoe County",
      centerName: "AllHealth Network",
      phone: "(303) 730-8858",
      address: "6509 S. Sante Fe Dr. Littleton, CO 80120",
    },
    {
      _id: "5",
      name: "Denver County",
      centerName: "Mental Health Center of Denver",
      phone: "(303) 504-7900",
      address: "4353 E. Colfax Ave. Denver, CO 80220",
    },
    {
      _id: "6",
      name: "Adams County",
      centerName: "Community Reach Center",
      phone: "(303) 853-3500",
      address: "2206 Victor St. Aurora, CO 80045",
    },
    {
      _id: "Other",
      name: "Other",
    },
  ];

  const handleLocalCrisisCenterChange = ({ currentTarget: input }) => {
    const obj = handleChange({ currentTarget: input });

    if (!input.value || input.value === "Other") {
      return;
    }

    const selectedOption = localCrisisCenterOptions.filter(
      (option) => option._id === input.value
    )[0];

    if (selectedOption) {
      obj.LocalCrisisCenterDetails[
        "@value"
      ] = `${selectedOption.centerName}\n${selectedOption.phone}\n${selectedOption.address}`;
      setData(obj);
    }
  };

  useEffect(() => {
    if (!clientid) {
      return;
    }

    const loadRecordedServices = async () => {
      const response = await getRecordedServices(clientid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setRecordedServices(response);
    };

    loadRecordedServices();
  }, [clientid]);

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <h5 className="text-center text-safy1-1">Discharge Data</h5>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Input
                      name="DateOfDischarge"
                      label="Date of Planned Discharge:"
                      type="date"
                      error={errors.DateOfDischarge}
                      value={data.DateOfDischarge["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Select
                      name="DischargeReason"
                      label="Reason for discharge:"
                      options={
                        props.formType === "TFC"
                          ? dischargeReasonTFCOptions
                          : dischargeReasonFPOptions
                      }
                      error={errors.DischargeReason}
                      value={data.DischargeReason["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {props.formType !== "MCR" ? (
                  <div className="row">
                    <div className="col">
                      <TextArea
                        name="DischargedTo"
                        label="Person/Agency to which client has been discharged:"
                        error={errors.DischargedTo}
                        value={data.DischargedTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <TextArea
                        name="DischargedToAddress"
                        label="Address of where client has been discharged:"
                        error={errors.DischargedToAddress}
                        value={
                          data.DischargedToAddress
                            ? data.DischargedToAddress["@value"]
                            : ""
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        rows="5"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
              {data.DischargeReason &&
                (data.DischargeReason["@value"] === "186460008" ||
                  data.DischargeReason["@value"] === "186460005" ||
                  data.DischargeReason["@value"] === "186460006") && (
                  <div>
                    <TextArea
                      name="BriefExplanation"
                      label="Brief explanation for * items:"
                      error={errors.BriefExplanation}
                      value={data.BriefExplanation["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                )}
              <div>
                <h5 className="text-center text-safy1-1">Aftercare Plan</h5>
              </div>
              <div className="mb-2">
                Continue the Following Services/Supports:
              </div>
              <div className="container">
                <div className="row bg-light mb-2 border-bottom">
                  <div className="col text-center">Services/Support</div>
                  <div className="col text-center">
                    Recommended Services/Support
                  </div>
                </div>
                <div className="row border-bottom mt-2 mt-4">
                  <div className="col">
                    <Checkbox
                      name="OutpatientMentalHealth"
                      label="Outpatient Mental Health"
                      checked={data.OutpatientMentalHealth["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.OutpatientMentalHealth["@value"] === "true" && (
                      <Input
                        name="OutpatientMentalHealthReferredTo"
                        error={errors.OutpatientMentalHealthReferredTo}
                        value={data.OutpatientMentalHealthReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                        maxCharacters={500}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="OutpatientSubstanceAbuse"
                      label="Outpatient Alcohol/Substance Abuse"
                      checked={
                        data.OutpatientSubstanceAbuse["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.OutpatientSubstanceAbuse["@value"] === "true" && (
                      <Input
                        name="OutpatientSubstanceAbuseReferredTo"
                        error={errors.OutpatientSubstanceAbuseReferredTo}
                        value={
                          data.OutpatientSubstanceAbuseReferredTo["@value"]
                        }
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="HousingAssistance"
                      label="Housing Assistance"
                      checked={data.HousingAssistance["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.HousingAssistance["@value"] === "true" && (
                      <Input
                        name="HousingAssistanceReferredTo"
                        error={errors.HousingAssistanceReferredTo}
                        value={data.HousingAssistanceReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="JobTraining"
                      label="Job Training"
                      checked={data.JobTraining["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.JobTraining["@value"] === "true" && (
                      <Input
                        name="JobTrainingReferredTo"
                        error={errors.JobTrainingReferredTo}
                        value={data.JobTrainingReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="EducationServices"
                      label="Education Services"
                      checked={data.EducationServices["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.EducationServices["@value"] === "true" && (
                      <Input
                        name="EducationServicesReferredTo"
                        error={errors.EducationServicesReferredTo}
                        value={data.EducationServicesReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="Tutoring"
                      label="Tutoring"
                      checked={data.Tutoring["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.Tutoring["@value"] === "true" && (
                      <Input
                        name="TutoringReferredTo"
                        error={errors.TutoringReferredTo}
                        value={data.TutoringReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="PsychiatricAppointment"
                      label="Psychiatric Appointment"
                      checked={data.PsychiatricAppointment["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.PsychiatricAppointment["@value"] === "true" && (
                      <Input
                        name="PsychiatricAppointmentReferredTo"
                        error={errors.PsychiatricAppointmentReferredTo}
                        value={data.PsychiatricAppointmentReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="Prescriptions"
                      label="Prescriptions and/or Renewal"
                      checked={data.Prescriptions["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.Prescriptions["@value"] === "true" && (
                      <Input
                        name="PrescriptionsReferredTo"
                        error={errors.PrescriptionsReferredTo}
                        value={data.PrescriptionsReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="MedicalServices"
                      label="Medical Services"
                      checked={data.MedicalServices["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.MedicalServices["@value"] === "true" && (
                      <Input
                        name="MedicalServicesReferredTo"
                        error={errors.MedicalServicesReferredTo}
                        value={data.MedicalServicesReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="Probation"
                      label="Probation/Parole Officer Contact"
                      checked={data.Probation["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.Probation["@value"] === "true" && (
                      <Input
                        name="ProbationReferredTo"
                        error={errors.ProbationReferredTo}
                        value={data.ProbationReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="LegalServices"
                      label="Legal Services"
                      checked={data.LegalServices["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.LegalServices["@value"] === "true" && (
                      <Input
                        name="LegalServicesReferredTo"
                        error={errors.LegalServicesReferredTo}
                        value={data.LegalServicesReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="PhysicianAppointment"
                      label="Physician Appointment"
                      checked={data.PhysicianAppointment["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.PhysicianAppointment["@value"] === "true" && (
                      <Input
                        name="PhysicianAppointmentReferredTo"
                        error={errors.PhysicianAppointmentReferredTo}
                        value={data.PhysicianAppointmentReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="WelfareSupport"
                      label="Welfare Support"
                      checked={data.WelfareSupport["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.WelfareSupport["@value"] === "true" && (
                      <Input
                        name="WelfareSupportReferredTo"
                        error={errors.WelfareSupportReferredTo}
                        value={data.WelfareSupportReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="FinancialSupport"
                      label="Financial Support"
                      checked={data.FinancialSupport["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.FinancialSupport["@value"] === "true" && (
                      <Input
                        name="FinancialSupportReferredTo"
                        error={errors.FinancialSupportReferredTo}
                        value={data.FinancialSupportReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="InHomeServices"
                      label=" In-home services"
                      checked={data.InHomeServices["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.InHomeServices["@value"] === "true" && (
                      <Input
                        name="InHomeServicesReferredTo"
                        error={errors.InHomeServicesReferredTo}
                        value={data.InHomeServicesReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Checkbox
                      name="OtherServices"
                      label="Other"
                      checked={data.OtherServices["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.OtherServices["@value"] === "true" && (
                      <Input
                        name="OtherServicesReferredTo"
                        error={errors.OtherServicesReferredTo}
                        value={data.OtherServicesReferredTo["@value"]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col mb-4">
                    National Suicide Hotline: 1-800-273-8255
                  </div>
                </div>
                <div className="row border-bottom mt-2">
                  <div className="col">
                    <Select
                      name="LocalCrisisCenter"
                      label="Local Crisis Center phone number and address:"
                      options={localCrisisCenterOptions}
                      error={errors.LocalCrisisCenter}
                      value={data.LocalCrisisCenter["@value"]}
                      onChange={handleLocalCrisisCenterChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <TextArea
                      rows="4"
                      name="LocalCrisisCenterDetails"
                      error={errors.LocalCrisisCenterDetails}
                      value={data.LocalCrisisCenterDetails["@value"]}
                      onChange={handleChange}
                      disabled={
                        formDisabled ||
                        data.LocalCrisisCenter["@value"] !== "Other"
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <h5 className="text-center text-safy1-1">
                  Summary of Services Provided
                </h5>
              </div>
              {data.SelectedProgram["@value"] &&
              data.SelectedProgram["@value"] !== "" &&
              recordedServices.filter(
                (service) =>
                  getFieldText(service.ccx_clientprogram) ===
                  data.SelectedProgram["@value"]
              ).length > 0 ? (
                <ServicesProvided
                  recordedServices={recordedServices.filter(
                    (service) =>
                      getFieldText(service.ccx_clientprogram) ===
                      data.SelectedProgram["@value"]
                  )}
                  exclusions={["Basic Skills Training", "Bed Day"]}
                ></ServicesProvided>
              ) : (
                "No services found"
              )}
              <TextArea
                name="ServicesNarrative"
                label="Narrative of Services Provided:"
                error={errors.ServicesNarrative}
                value={data.ServicesNarrative?.["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="mt-4">
                <h5 className="text-center text-safy1-1">
                  Discharge Summary Narrative
                </h5>
              </div>
              <div className="mt-4">
                {props.formType !== "MCR" ? (
                  <TextArea
                    name="PlacementHistory"
                    label="Placement History:"
                    error={errors.PlacementHistory}
                    value={data.PlacementHistory["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
                <TextArea
                  name="Behavior"
                  label="Behavior (regulation and coping):"
                  error={errors.Behavior}
                  value={data.Behavior["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="SocialInteraction"
                  label="Social and Peer Interaction:"
                  error={errors.SocialInteraction}
                  value={data.SocialInteraction["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {props.formType !== "MCR" ? (
                  <>
                    <TextArea
                      name="ResponseToCaregivers"
                      label="Response to Caregivers or Redirection:"
                      error={errors.ResponseToCaregivers}
                      value={data.ResponseToCaregivers["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <TextArea
                      name="SchoolInformation"
                      label="School Information and Participation:"
                      error={errors.SchoolInformation}
                      value={data.SchoolInformation["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
                <TextArea
                  name="AdditionalAccomplishments"
                  label="Additional Accomplishments:"
                  error={errors.AdditionalAccomplishments}
                  value={data.AdditionalAccomplishments["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="FollowUp"
                  label="Follow-up/Scheduled Appointments:"
                  error={errors.FollowUp}
                  value={data.FollowUp["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default COTFCDischargeSummaryv2;
